import {
    CheckOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    DownloadOutlined,
    InboxOutlined,
    PlayCircleOutlined,
    RightOutlined,
    SaveOutlined,
    SearchOutlined,
    SwapOutlined,
    ThunderboltOutlined,
    UndoOutlined,
    BarChartOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { Alert, Button, DatePicker, Form, Input, message, Modal, Select, Spin, Table, Tabs, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth } from '../../../context/DataContext';
import {
    ageCategories,
    brassAgeCategoriesSolo,
    electoneAgeCategoriesSolo,
    ensembleAgeCategories,
    guitarAgeCategoriesSolo,
    guzhengAgeCategoriesEnsemble,
    guzhengAgeCategoriesSolo,
    harpAgeCategoriesSolo,
    percussionAgeCategoriesSolo,
    stringAgeCategoriesSolo,
    vocalAgeCategoriesSolo,
    woodwinAgeCategoriesSolo,
} from '../../../constant/RegisterPageConst';
import { db } from '../../../firebase';
import apis from '../../../apis';
import { uploadFullSeatLayout } from '../../../Pages/AdminDashboard/SeatEvent';
import { formatDuration, getAssignedVideoTotal, getSessionDurationSeconds, getVideoDurationSeconds } from '../../../utils/sessionDuration';
import { formatCommentSheetAward } from '../../../utils/csvVariableSafeUtil';
import OrchestraAttendanceTable from './OrchestraAttendanceTable';
import GroupRegistrantSummary from './GroupRegistrantSummary';
import AttendanceStatusChip, { AttendanceStatusFilter } from './AttendanceStatusChip';
import { matchesAttendanceStatus, getVisibleAttendanceEntries } from '../../../utils/registrantAttendance';
import { ATTENDANCE_PURPOSE, isAttendanceGroup, getPerformanceAssignedIds, updateAttendanceMembers } from '../../../utils/sessionAssignmentGroups';
import { getRegistrantEventId, loadGuestRegistrants, loadAssignedGuestRegistrants } from '../../../utils/guestRegistrants';
import './SessionAssignmentManager.css';

const ITEM_TYPE = 'REGISTRANT_CARD';

// Session colors for visual distinction
const SESSION_COLORS = [
    '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6',
    '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#a855f7',
    '#10b981', '#e11d48', '#0ea5e9', '#eab308', '#7c3aed',
];

// --- Helper: Get performer name from registrant ---
const getRegistrantName = (reg) => {
    if (!reg) return 'Unknown';
    // Old format (Registrants collection)
    if (reg.name && !reg.performers) return reg.name;
    // New format (Registrants2025)
    const p = reg.performers?.[0];
    if (!p) return reg.name || 'Unknown';
    if (typeof p === 'string') return p.trim() || 'Unknown';
    return p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || 'Unknown';
};

const getRegistrantEmail = (reg) => {
    if (!reg) return '';
    const p = reg.performers?.[0];
    return p?.email || reg.email || '';
};

const getRegistrantCity = (reg) => {
    if (!reg) return '';
    const p = reg.performers?.[0];
    return p?.city || reg.city || '';
};

// Match the Jury Dashboard's streaming path for saved S3 keys and URLs.
const getPerformanceVideoUrl = (rawLink) => {
    if (typeof rawLink !== 'string' || !rawLink.trim()) return '';
    const link = rawLink.trim();
    let encodedPath;
    if (/^https?:\/\//i.test(link)) {
        encodedPath = new URL(link).pathname;
    } else {
        // Registration stores s3://bucket/<raw key>, including the original filename.
        // Strip only the bucket so # and ? in filenames remain part of the key.
        const s3Match = /^s3:\/\/[^/]+\/(.+)$/i.exec(link);
        if (/^s3:/i.test(link) && !s3Match) throw new Error('Invalid S3 video link');
        const key = s3Match ? s3Match[1] : link;
        encodedPath = key.split('/').map(encodeURIComponent).join('/');
    }
    return `https://dk423kvk3sha9.cloudfront.net/${encodedPath.replace(/^\/+/, '')}`;
};

const PlayVideoButton = ({ registrant, onPlay }) => {
    const hasVideo = typeof registrant.videoPerformanceS3Link === 'string'
        && Boolean(registrant.videoPerformanceS3Link.trim());
    return (
        <Tooltip title={hasVideo ? 'Play performance video' : 'No performance video available'}>
            <span onClick={e => e.stopPropagation()}
                onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}>
                <Button size="small" icon={<PlayCircleOutlined />} disabled={!hasVideo}
                    aria-label={`Play performance video for ${getDisplayName(registrant)}`}
                    onClick={e => { e.stopPropagation(); onPlay(registrant); }}>
                    Play video
                </Button>
            </span>
        </Tooltip>
    );
};

// --- Helper: Check if registrant is ensemble ---
const isEnsembleRegistrant = (reg) => {
    if (!reg) return false;
    if (reg.isEnsemble !== undefined) return !!reg.isEnsemble;
    if ((reg.PerformanceCategory || reg.performanceCategory || '').trim().toLowerCase() === 'ensemble') return true;
    if ((reg.competitionCategory || '').toLowerCase().includes('ensemble')) return true;
    if (Array.isArray(reg.performers) && reg.performers.length > 1) return true;
    if (Array.isArray(reg.performerNames) && reg.performerNames.length > 1) return true;
    if (Number(reg.totalPerformer || 0) > 1) return true;
    return false;
};

// --- Helper: Get all performer/kid names from registrant ---
const getPerformerNames = (reg) => {
    if (!reg) return [];
    if (Array.isArray(reg.performerNames) && reg.performerNames.length > 0) {
        return reg.performerNames;
    }
    if (Array.isArray(reg.performers) && reg.performers.length > 0) {
        return reg.performers
            .map(p => {
                if (!p) return '';
                if (typeof p === 'string') return p.trim();
                return (p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || '').trim();
            })
            .filter(Boolean);
    }
    const defaultName = getRegistrantName(reg);
    return defaultName && defaultName !== 'Unknown' ? [defaultName] : [];
};

// --- Helper: Get formatted display title ---
const getDisplayName = (reg) => {
    if (!reg) return 'Unknown';
    const isEnsemble = isEnsembleRegistrant(reg);
    const pNames = getPerformerNames(reg);
    if (isEnsemble && pNames.length > 1) {
        if (pNames.length === 2) {
            return `${pNames[0]} & ${pNames[1]}`;
        }
        return `${pNames.slice(0, -1).join(', ')} & ${pNames[pNames.length - 1]}`;
    }
    return getRegistrantName(reg);
};

// --- Helper: Check if registrant or any of its ensemble kids matches search ---
const doesRegistrantMatchSearch = (reg, term) => {
    if (!term) return true;
    const lower = term.toLowerCase().trim();
    if (!lower) return true;
    const name = getRegistrantName(reg).toLowerCase();
    const display = getDisplayName(reg).toLowerCase();
    const teacher = (reg.teacherName || '').toLowerCase();
    const repertoire = (reg.repertoireTitle || '').toLowerCase();
    const category = (reg.competitionCategory || '').toLowerCase();
    const kidNames = getPerformerNames(reg);
    const hasMatchingKid = kidNames.some(p => p.toLowerCase().includes(lower));
    return name.includes(lower) || display.includes(lower) || teacher.includes(lower) || repertoire.includes(lower) || category.includes(lower) || hasMatchingKid;
};

const isFailRegistrant = reg => String(reg.finalAward || reg.achievement || '').trim().toLowerCase() === 'fail';

const ageCategoryLabels = {
    ...ageCategories,
    ...ensembleAgeCategories,
    ...stringAgeCategoriesSolo,
    ...vocalAgeCategoriesSolo,
    ...woodwinAgeCategoriesSolo,
    ...percussionAgeCategoriesSolo,
    ...guitarAgeCategoriesSolo,
    ...brassAgeCategoriesSolo,
    ...electoneAgeCategoriesSolo,
    ...harpAgeCategoriesSolo,
    ...guzhengAgeCategoriesSolo,
    ...guzhengAgeCategoriesEnsemble,
};

const getAgeCategoryLabel = ageCategory => ageCategoryLabels[ageCategory] || ageCategory || 'Not set';

const TEACHER_AWARD_TIERS = ['Sapphire', 'Diamond', 'Gold', 'Silver', 'Fail'];

const getTeacherAwardStats = registrants => {
    const teachers = new Map();
    registrants.forEach(reg => {
        const name = String(reg.teacherName || '').trim();
        if (!name) return;
        const key = name.toLocaleLowerCase();
        if (!teachers.has(key)) {
            teachers.set(key, {
                key, name, total: 0, winners: 0, other: 0,
                Sapphire: 0, Diamond: 0, Gold: 0, Silver: 0, Fail: 0,
            });
        }
        const stats = teachers.get(key);
        const award = formatCommentSheetAward(reg.finalAward || reg.achievement);
        stats.total += 1;
        if (TEACHER_AWARD_TIERS.includes(award)) stats[award] += 1;
        else stats.other += 1;
        if (TEACHER_AWARD_TIERS.includes(award) && award !== 'Fail') stats.winners += 1;
    });
    return Array.from(teachers.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
};

const teacherStatsColumns = [
    { title: 'Teacher', dataIndex: 'name', key: 'name', fixed: 'left', width: 220 },
    { title: 'Total (including Fail)', dataIndex: 'total', key: 'total', width: 160 },
    { title: 'Winners (excluding Fail)', dataIndex: 'winners', key: 'winners', width: 180 },
    ...TEACHER_AWARD_TIERS.map(tier => ({ title: tier, dataIndex: tier, key: tier, width: 100 })),
    { title: 'Other / no award', dataIndex: 'other', key: 'other', width: 150 },
];

// ======================================================
// DraggableCard Component
// ======================================================
const DraggableCard = ({
    registrant,
    index,
    sessionId,
    moveWithinSession,
    onRemove,
    onPlayVideo,
    isSwapMode,
    isSwapSelected,
    onSwapSelect,
    searchTerm = '',
    readOnly = false,
}) => {
    const ref = useRef(null);
    const displayName = getDisplayName(registrant);
    const isEnsemble = isEnsembleRegistrant(registrant);
    const performerNames = getPerformerNames(registrant);
    const isCardMatch = Boolean(searchTerm && doesRegistrantMatchSearch(registrant, searchTerm));
    const achievement = registrant.finalAward || registrant.achievement || '';
    const achievementClass = achievement.toLowerCase();

    const [{ isDragging }, drag] = useDrag({
        type: ITEM_TYPE,
        item: () => ({ registrantId: registrant.id, sourceSessionId: sessionId, index }),
        canDrag: !readOnly,
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        canDrop: () => !readOnly,
        hover(item, monitor) {
            if (readOnly) return;
            if (!ref.current || !sessionId || item.sourceSessionId !== sessionId) return;
            const dragIdx = item.index;
            const hoverIdx = index;
            if (dragIdx === hoverIdx) return;

            const hoverRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverRect.top;

            if (dragIdx < hoverIdx && hoverClientY < hoverMiddleY) return;
            if (dragIdx > hoverIdx && hoverClientY > hoverMiddleY) return;

            moveWithinSession(sessionId, dragIdx, hoverIdx);
            item.index = hoverIdx;
        },
    });

    drag(drop(ref));

    return (
        <div
            ref={ref}
            className={`sam-registrant-card ${isDragging ? 'dragging' : ''} ${isSwapSelected ? 'swap-selected' : ''} ${isCardMatch && searchTerm ? 'search-match' : ''}`}
            onClick={isSwapMode ? () => onSwapSelect(registrant.id, sessionId) : undefined}
            style={{ cursor: readOnly ? 'default' : isSwapMode ? 'pointer' : 'grab' }}
        >
            {sessionId && <div className="order-number">{index + 1}</div>}
            <div className="card-info">
                <div className="card-name-row">
                    <span className="card-name" title={displayName}>{displayName}</span>
                    <AttendanceStatusChip registrant={registrant} />
                    {isEnsemble && (
                        <span className="sam-ensemble-badge">
                            Ensemble ({performerNames.length})
                        </span>
                    )}
                </div>
                {isEnsemble && performerNames.length > 0 && (
                    <div className="sam-ensemble-members">
                        <span className="sam-ensemble-label">👥 Kids:</span>
                        <div className="sam-ensemble-tags">
                            {performerNames.map((kidName, kidIdx) => {
                                const isKidMatch = Boolean(searchTerm && kidName.toLowerCase().includes(searchTerm.toLowerCase().trim()));
                                return (
                                    <span
                                        key={kidIdx}
                                        className={`sam-ensemble-kid-tag ${isKidMatch ? 'highlighted' : ''}`}
                                    >
                                        {kidName}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className="card-details">
                    <span>Event: {getRegistrantEventId(registrant)}</span>
                    {achievement && <span className={`achievement-tag ${achievementClass}`}>{achievement}</span>}
                    {registrant.teacherName && <span>🎓 {registrant.teacherName}</span>}
                    {registrant.repertoireTitle && <span>🎼 {registrant.repertoireTitle}</span>}
                    {registrant.competitionCategory && <span>🎵 {registrant.competitionCategory}</span>}
                    <span>⏱ Video: {formatDuration(getVideoDurationSeconds(registrant))}</span>
                    {getRegistrantCity(registrant) && <span>📍 {getRegistrantCity(registrant)}</span>}
                </div>
            </div>
            <div className="card-actions">
                <PlayVideoButton registrant={registrant} onPlay={onPlayVideo} />
                {sessionId && onRemove && (
                    <Tooltip title="Remove from session">
                        <Button size="small" type="text" danger icon={<CloseOutlined />}
                            onClick={(e) => { e.stopPropagation(); onRemove(registrant.id, sessionId); }} />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

// Orchestra attendance groups keep registered members organized; they are not ticketable performances.
const GROUP_PURPOSE_OPTIONS = [
    { value: 'competition', label: 'Competition performance' },
    { value: ATTENDANCE_PURPOSE, label: 'Orchestra attendance' },
];

// ======================================================
// SessionDropZone Component
// ======================================================
const SessionDropZone = ({
    sessionId,
    sessionName,
    sessionTime,
    sessionColor,
    registrants,
    index: sessionIndex,
    moveWithinSession,
    onDropFromPool,
    onRemove,
    onPlayVideo,
    isExpanded,
    onToggle,
    isSwapMode,
    swapSelections,
    onSwapSelect,
    searchTerm = '',
    attendanceFilter = 'all',
    readOnly = false,
}) => {
    const [{ isOver }, drop] = useDrop({
        accept: ITEM_TYPE,
        canDrop: () => !readOnly,
        drop: (item) => {
            if (readOnly) return;
            if (item.sourceSessionId !== sessionId) {
                onDropFromPool(item.registrantId, item.sourceSessionId, sessionId);
            }
        },
        collect: (monitor) => ({ isOver: monitor.isOver() }),
    });

    const matchingCount = useMemo(() => {
        if (!searchTerm) return 0;
        return registrants.filter(r => matchesAttendanceStatus(r, attendanceFilter) && doesRegistrantMatchSearch(r, searchTerm)).length;
    }, [registrants, searchTerm, attendanceFilter]);

    const hasMatch = matchingCount > 0;
    const visibleEntries = getVisibleAttendanceEntries(registrants, attendanceFilter);
    const totalSeconds = getAssignedVideoTotal(registrants);
    const sessionSeconds = getSessionDurationSeconds(sessionTime);
    const unknownCount = registrants.filter(reg => getVideoDurationSeconds(reg) === null).length;

    return (
        <div className={`sam-session-card ${isOver ? 'drag-over' : ''} ${hasMatch ? 'search-match-session' : ''}`}>
            <div className="sam-session-header" onClick={onToggle}>
                <div className="sam-session-header-left">
                    <div className="session-color-dot" style={{ background: sessionColor }} />
                    <div className="sam-session-heading">
                        <h4>{sessionName}</h4>
                        <Tag className="sam-session-time-tag" icon={<ClockCircleOutlined />} color={sessionTime ? 'gold' : 'orange'}>
                            {sessionTime ? `Assigned time: ${sessionTime}` : 'Time not assigned'}
                        </Tag>
                    </div>
                </div>
                <div className="sam-session-header-right">
                    <div className="sam-session-meta">
                        {hasMatch && (
                            <Tag color="gold" className="sam-search-match-tag">
                                🔍 {matchingCount} match{matchingCount > 1 ? 'es' : ''}
                            </Tag>
                        )}
                        <Tag className="sam-session-meta-tag" color="blue">{registrants.length} registrants</Tag>
                        {attendanceFilter !== 'all' && <Tag>{visibleEntries.length} shown</Tag>}
                        <Tag className="sam-session-meta-tag" color={sessionSeconds !== null && totalSeconds > sessionSeconds ? 'warning' : 'default'}>
                            Video total: {formatDuration(totalSeconds)}{sessionSeconds !== null ? ` / ${formatDuration(sessionSeconds)}` : ''}
                        </Tag>
                        {sessionSeconds !== null && totalSeconds > sessionSeconds && (
                            <Tag color="warning">Over by {formatDuration(totalSeconds - sessionSeconds)}</Tag>
                        )}
                        {unknownCount > 0 && <Tag color="default">{unknownCount} unknown duration{unknownCount > 1 ? 's' : ''}</Tag>}
                    </div>
                    <RightOutlined className={`sam-session-expand-icon ${isExpanded ? 'expanded' : ''}`} />
                </div>
                <GroupRegistrantSummary registrants={registrants} groupName={sessionName} />
            </div>
            {isExpanded && (
                <div ref={drop} className={`sam-session-body ${isOver ? 'drag-over' : ''}`}>
                    {visibleEntries.length === 0 ? (
                        <div className="sam-session-body-empty">
                            <InboxOutlined style={{ marginRight: 8 }} /> {registrants.length
                                ? 'No registrants match this attendance filter' : readOnly ? 'No registrants' : 'Drop registrants here'}
                        </div>
                    ) : (
                        visibleEntries.map(({ registrant: reg, index: idx }) => (
                            <DraggableCard
                                key={reg.id}
                                registrant={reg}
                                index={idx}
                                sessionId={sessionId}
                                moveWithinSession={moveWithinSession}
                                onRemove={onRemove}
                                onPlayVideo={onPlayVideo}
                                isSwapMode={isSwapMode}
                                isSwapSelected={swapSelections.some(s => s.registrantId === reg.id)}
                                onSwapSelect={onSwapSelect}
                                searchTerm={searchTerm}
                                readOnly={readOnly}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ======================================================
// Main SessionAssignmentManager Component
// ======================================================
const SessionAssignmentManager = () => {
    const { currentEventId } = useAuth();

    // --- State ---
    const [eventId, setEventId] = useState(currentEventId || 'APCS2026');
    const [allRegistrants, setAllRegistrants] = useState([]);
    const [guestRegistrants, setGuestRegistrants] = useState([]);
    const [registrantTab, setRegistrantTab] = useState('current');
    const [sourceEventId, setSourceEventId] = useState('APCS2025');
    const [guestsLoaded, setGuestsLoaded] = useState(false);
    const [guestsLoading, setGuestsLoading] = useState(false);
    const [guestLoadError, setGuestLoadError] = useState(null);
    const guestRequest = useRef(0);
    const [sessions, setSessions] = useState([]); // [{id, name}]
    const [initialSessions, setInitialSessions] = useState([]);
    const [assignments, setAssignments] = useState({}); // {sessionId: [registrantObj, ...]}
    const [planningState, setPlanningState] = useState(null);
    const [eventVenues, setEventVenues] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [loadedEventId, setLoadedEventId] = useState(null);
    const [groupForm] = Form.useForm();
    const selectedGroupVenue = Form.useWatch('venueId', groupForm);
    const selectedGroupDate = Form.useWatch('date', groupForm);
    const selectedGroupPurpose = Form.useWatch('purpose', groupForm);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [planningPreview, setPlanningPreview] = useState(null);
    const [planningBusy, setPlanningBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [attendanceFilter, setAttendanceFilter] = useState('all');
    const [expandedSessions, setExpandedSessions] = useState({});
    const [isSwapMode, setIsSwapMode] = useState(false);
    const [swapSelections, setSwapSelections] = useState([]); // [{registrantId, sessionId}]
    const [initialAssignments, setInitialAssignments] = useState(null);
    const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);
    const [isTeacherStatsOpen, setIsTeacherStatsOpen] = useState(false);
    const [teacherStatsSearch, setTeacherStatsSearch] = useState('');
    const [teacherStatsCategory, setTeacherStatsCategory] = useState(null);
    const [isTeacherStatsExporting, setIsTeacherStatsExporting] = useState(false);
    const [overviewVenue, setOverviewVenue] = useState(null);
    const [overviewTab, setOverviewTab] = useState('registrants');
    const [overviewSearchTerm, setOverviewSearchTerm] = useState('');
    const [activeVideo, setActiveVideo] = useState(null);
    const [videoError, setVideoError] = useState(false);

    useEffect(() => {
        setActiveVideo(null);
        setVideoError(false);
    }, [eventId]);

    const handlePlayVideo = useCallback(registrant => {
        try {
            const url = getPerformanceVideoUrl(registrant.videoPerformanceS3Link);
            if (!url) return;
            setVideoError(false);
            setActiveVideo({ registrant, url });
        } catch (error) {
            message.error('Could not open the performance video. The saved video link is invalid.');
        }
    }, []);
    const isDirty = useMemo(() => initialAssignments !== null && (
        JSON.stringify(assignments) !== JSON.stringify(initialAssignments)
        || JSON.stringify(sessions) !== JSON.stringify(initialSessions)
    ), [assignments, initialAssignments, sessions, initialSessions]);

    const performanceSessions = useMemo(() => sessions.filter(session => !isAttendanceGroup(session)), [sessions]);
    const attendanceSessions = useMemo(() => sessions.filter(isAttendanceGroup), [sessions]);
    // Attendance membership never consumes a performance assignment or the left-hand pool.
    const assignedIds = useMemo(() => getPerformanceAssignedIds(sessions, assignments), [sessions, assignments]);
    const boardRegistrants = useMemo(() => [...new Map([...allRegistrants, ...guestRegistrants]
        .map(registrant => [registrant.id, registrant])).values()], [allRegistrants, guestRegistrants]);
    const eligibleAttendanceRegistrants = useMemo(() => boardRegistrants.filter(reg => !isFailRegistrant(reg)), [boardRegistrants]);
    const sourceRegistrants = useMemo(() => registrantTab === 'other'
        ? guestRegistrants.filter(reg => getRegistrantEventId(reg) === sourceEventId) : allRegistrants,
    [registrantTab, guestRegistrants, sourceEventId, allRegistrants]);
    const eligibleSourceRegistrants = useMemo(() => sourceRegistrants.filter(reg => !assignedIds.has(reg.id) && !isFailRegistrant(reg)),
    [sourceRegistrants, assignedIds]);

    // --- Derived: unassigned registrants ---
    const eligibleUnassignedRegistrants = useMemo(() =>
        allRegistrants.filter(reg => !assignedIds.has(reg.id) && !isFailRegistrant(reg)),
    [allRegistrants, assignedIds]);

    const unassignedRegistrants = useMemo(() => {
        let filtered = eligibleSourceRegistrants;
        if (searchTerm) {
            filtered = filtered.filter(r => doesRegistrantMatchSearch(r, searchTerm));
        }
        if (categoryFilter) {
            filtered = filtered.filter(r => r.competitionCategory === categoryFilter);
        }
        return filtered.filter(reg => matchesAttendanceStatus(reg, attendanceFilter));
    }, [eligibleSourceRegistrants, searchTerm, categoryFilter, attendanceFilter]);

    // --- Derived: category options ---
    const categoryOptions = useMemo(() => {
        const cats = new Set();
        allRegistrants.forEach(r => { if (r.competitionCategory) cats.add(r.competitionCategory); });
        return Array.from(cats).sort().map(c => ({ value: c, label: c }));
    }, [allRegistrants]);

    const poolCategoryOptions = [...new Set(sourceRegistrants.map(reg => reg.competitionCategory).filter(Boolean))]
        .sort().map(category => ({ value: category, label: category }));

    const loadOtherEvent = async () => {
        const requestId = ++guestRequest.current;
        setGuestsLoading(true);
        setGuestLoadError(null);
        try {
            const registrants = await loadGuestRegistrants(sourceEventId);
            if (requestId !== guestRequest.current) return;
            setGuestRegistrants(registrants);
            setGuestsLoaded(true);
        } catch (error) {
            if (requestId === guestRequest.current) setGuestLoadError(error.message || 'Could not load other event registrants.');
        } finally {
            if (requestId === guestRequest.current) setGuestsLoading(false);
        }
    };

    const teacherStats = useMemo(() => getTeacherAwardStats(
        teacherStatsCategory
            ? allRegistrants.filter(reg => reg.competitionCategory === teacherStatsCategory)
            : allRegistrants
    ), [allRegistrants, teacherStatsCategory]);
    const visibleTeacherStats = useMemo(() => teacherStats.filter(row =>
        row.name.toLocaleLowerCase().includes(teacherStatsSearch.trim().toLocaleLowerCase())),
    [teacherStats, teacherStatsSearch]);
    const teacherStatsTotals = useMemo(() => visibleTeacherStats.reduce((totals, row) => {
        ['total', 'winners', 'other', ...TEACHER_AWARD_TIERS].forEach(key => {
            totals[key] += row[key];
        });
        return totals;
    }, { total: 0, winners: 0, other: 0, Sapphire: 0, Diamond: 0, Gold: 0, Silver: 0, Fail: 0 }),
    [visibleTeacherStats]);

    // --- Derived: Overview — unique venues and per-venue sessions ---
    const overviewVenues = useMemo(() => {
        const map = {};
        performanceSessions.forEach(s => {
            if (!map[s.venue]) map[s.venue] = s.venueLabel || s.venue;
        });
        return Object.entries(map).map(([id, label]) => ({ id, label }));
    }, [performanceSessions]);

    const overviewSessionsForVenue = useMemo(() => {
        if (!overviewVenue) return [];
        return performanceSessions.filter(s => s.venue === overviewVenue);
    }, [performanceSessions, overviewVenue]);

    // --- Fetch sessions from events/{eventId} ---
    const fetchSessions = useCallback(async (eid) => {
            const [eventDoc, planningResponse] = await Promise.all([
                getDoc(doc(db, 'events', eid)),
                apis.competitionPlanning.getState(eid),
            ]);
            if (planningResponse?.error) throw new Error(planningResponse.error.data?.message || 'Could not load planning state.');
            if (!eventDoc.exists()) throw new Error(`No event found for ${eid}`);
            const data = eventDoc.data();
            const plan = planningResponse.data;
            const sessionList = [];
            const venueList = data.venues || [];

            if (plan.status !== 'legacy') {
                (plan.groups || []).forEach(group => {
                    const venueLabel = venueList.find(venue => venue.id === group.venueId)?.label || group.venueId;
                    const slot = (plan.draftSlots || []).find(item => item.slotId === group.slotId);
                    const start = slot?.start || group.start;
                    const end = slot?.end || group.end;
                    const time = start && end ? `${start}-${end}` : '';
                    const purpose = group.purpose || 'competition';
                    sessionList.push({
                        id: group.groupId,
                        name: `${group.label} — ${venueLabel} — ${group.date} — ${purpose === ATTENDANCE_PURPOSE ? 'Orchestra attendance' : time || 'Time pending'}`,
                        label: group.label, purpose, venue: group.venueId, venueLabel, date: group.date,
                        slotId: group.slotId || null, start: start || null, end: end || null, time,
                    });
                });
            } else {
                // Legacy sessions are already published on the event.
                venueList.forEach(venue => {
                    const venueSessions = venue.sessions || {};
                    const venueLabel = venue.label || venue.id;
                    Object.keys(venueSessions).forEach(date => {
                        const times = venueSessions[date] || [];
                        times.forEach(time => {
                            const id = `${venue.id}_${date}_${time}`;
                            sessionList.push({
                                id, name: `${venueLabel} — ${date} — ${time}`,
                                venue: venue.id, venueLabel, date, time,
                            });
                        });
                    });
                });
            }

            // Sort by date then time
            if (plan.status === 'legacy') sessionList.sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                return a.time.localeCompare(b.time);
            });

            return { plan, sessions: sessionList, venues: venueList };
    }, []);

    // --- Fetch registrants ---
    const fetchRegistrants = useCallback(async (eid, isPlanning) => {
            const q = isPlanning
                ? query(collection(db, 'Registrants2025'), where('eventId', '==', eid))
                : query(collection(db, 'Registrants2025'), orderBy('createdAt'));
            const snap = await getDocs(q);
            let docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
            // Missing eventId belongs to APCS2025, never the selected current event.
            if (eid) {
                docs = docs.filter(d => getRegistrantEventId(d) === eid);
            }
            return docs;
    }, []);

    // --- Load saved assignments ---
    const loadAssignments = useCallback(async (eid, registrantMap, plan) => {
            if (plan.status !== 'legacy') {
                const hydrated = {};
                (plan.groups || []).forEach(group => {
                    hydrated[group.groupId] = (group.registrantIds || []).map(id =>
                        registrantMap[id] || { id, name: `Missing registrant (${id})`, missing: true });
                });
                return hydrated;
            }
            const res = await apis.session.getAssignments(eid);
            if (res?.error) throw new Error(res.error.data?.message || 'Could not load saved assignments.');
            if (res && res.data && res.data.assignments) {
                const saved = res.data.assignments || {};
                // Rehydrate: replace stale snapshots with current registrant data
                const hydrated = {};
                Object.keys(saved).forEach(sessionId => {
                    hydrated[sessionId] = (saved[sessionId] || [])
                        .map(entry => registrantMap[entry.registrantId])
                        .filter(Boolean);
                });
                return hydrated;
            }
            return {};
    }, []);

    // --- Warn on unsaved changes ---
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // --- Initial load ---
    useEffect(() => {
        let active = true;
        const init = async () => {
            guestRequest.current += 1;
            setGuestRegistrants([]);
            setGuestsLoaded(false);
            setGuestsLoading(false);
            setGuestLoadError(null);
            setRegistrantTab('current');
            setSearchTerm('');
            setCategoryFilter(null);
            setAttendanceFilter('all');
            setLoading(true);
            setLoadError(null);
            setInitialAssignments(null);
            setPlanningPreview(null);
            setIsSwapMode(false);
            setSwapSelections([]);
            try {
                const { plan, sessions: loadedSessions, venues } = await fetchSessions(eventId);
                const registrants = await fetchRegistrants(eventId, plan.status !== 'legacy');
                const guests = await loadAssignedGuestRegistrants(plan.groups || [], registrants);
                const map = Object.fromEntries([...registrants, ...guests].map(registrant => [registrant.id, registrant]));
                const loadedAssignments = await loadAssignments(eventId, map, plan);
                if (!active) return;
                setPlanningState(plan);
                setEventVenues(venues);
                setSessions(loadedSessions);
                setInitialSessions(loadedSessions);
                setExpandedSessions(loadedSessions.length ? { [loadedSessions[0].id]: true } : {});
                setAllRegistrants(registrants);
                setGuestRegistrants(guests);
                setAssignments(loadedAssignments);
                setInitialAssignments(loadedAssignments);
                setLoadedEventId(eventId);
            } catch (error) {
                if (active) {
                    console.error('Failed to load session board:', error);
                    setLoadError(error.message || 'Could not load the session board.');
                }
            }
            if (active) setLoading(false);
        };
        init();
        return () => { active = false; guestRequest.current += 1; };
    }, [eventId, fetchSessions, fetchRegistrants, loadAssignments, reloadVersion]);

    // --- DnD: Move within same session (reorder) ---
    const moveWithinSession = useCallback((sessionId, dragIdx, hoverIdx) => {
        setAssignments(prev => {
            const regs = [...(prev[sessionId] || [])];
            const [removed] = regs.splice(dragIdx, 1);
            regs.splice(hoverIdx, 0, removed);
            return { ...prev, [sessionId]: regs };
        });
    }, []);

    // --- DnD: Drop from pool or another session ---
    const handleDrop = useCallback((registrantId, sourceSessionId, targetSessionId) => {
        if (!performanceSessions.some(session => session.id === targetSessionId)
            || (sourceSessionId && !performanceSessions.some(session => session.id === sourceSessionId))) return;
        setAssignments(prev => {
            const next = { ...prev };
            // Remove from source
            if (sourceSessionId && next[sourceSessionId]) {
                next[sourceSessionId] = next[sourceSessionId].filter(r => r.id !== registrantId);
            }
            // Find registrant
            const reg = boardRegistrants.find(r => r.id === registrantId);
            if (!reg) return prev;
            // Add to target
            next[targetSessionId] = [...(next[targetSessionId] || []).filter(r => r.id !== registrantId), reg];
            return next;
        });
    }, [boardRegistrants, performanceSessions]);

    const handleAttendanceMembersChange = (groupId, ids) => {
        if (readOnly) return;
        setAssignments(previous => updateAttendanceMembers(sessions, previous, boardRegistrants, groupId, ids));
        setPlanningPreview(null);
    };

    // --- Remove from session ---
    const handleRemove = useCallback((registrantId, sessionId) => {
        setAssignments(prev => ({
            ...prev,
            [sessionId]: (prev[sessionId] || []).filter(r => r.id !== registrantId),
        }));
    }, []);

    // --- Swap logic ---
    const handleSwapSelect = useCallback((registrantId, sessionId) => {
        setSwapSelections(prev => {
            const exists = prev.findIndex(s => s.registrantId === registrantId);
            if (exists >= 0) return prev.filter((_, i) => i !== exists);
            if (prev.length >= 2) return [{ registrantId, sessionId }];
            return [...prev, { registrantId, sessionId }];
        });
    }, []);

    const executeSwap = useCallback(() => {
        if (swapSelections.length !== 2) return;
        const [a, b] = swapSelections;
        setAssignments(prev => {
            const next = { ...prev };
            const listA = [...(next[a.sessionId] || [])];
            const listB = [...(next[b.sessionId] || [])];
            const idxA = listA.findIndex(r => r.id === a.registrantId);
            const idxB = listB.findIndex(r => r.id === b.registrantId);
            if (idxA < 0 || idxB < 0) return prev;

            if (a.sessionId === b.sessionId) {
                [listA[idxA], listA[idxB]] = [listA[idxB], listA[idxA]];
                next[a.sessionId] = listA;
            } else {
                const temp = listA[idxA];
                listA[idxA] = listB[idxB];
                listB[idxB] = temp;
                next[a.sessionId] = listA;
                next[b.sessionId] = listB;
            }
            return next;
        });
        setSwapSelections([]);
        message.success('Swapped successfully!');
    }, [swapSelections]);

    // --- Auto-assign ---
    const handleAutoAssign = useCallback(() => {
        // Auto-assign distributes competition performances; attendance groups are filled manually.
        if (performanceSessions.length === 0) { message.warning('No sessions available'); return; }
        const unassigned = eligibleUnassignedRegistrants;
        if (unassigned.length === 0) { message.info('No eligible unassigned registrants'); return; }

        const perSession = Math.ceil(unassigned.length / performanceSessions.length);
        const newAssignments = { ...assignments };
        let idx = 0;
        performanceSessions.forEach(session => {
            const existing = newAssignments[session.id] || [];
            const toAdd = unassigned.slice(idx, idx + perSession);
            newAssignments[session.id] = [...existing, ...toAdd];
            idx += perSession;
        });
        setAssignments(newAssignments);
        message.success(`Auto-assigned ${unassigned.length} registrants across ${performanceSessions.length} sessions`);
    }, [performanceSessions, eligibleUnassignedRegistrants, assignments]);

    const isPlanning = planningState && planningState.status !== 'legacy';
    const readOnly = Boolean(isPlanning && planningState.status !== 'draft');

    const openGroup = (session = null, purpose = 'competition') => {
        if (readOnly) return;
        setEditingGroupId(session?.id || null);
        groupForm.resetFields();
        groupForm.setFieldsValue({
            label: session?.label || `Group ${sessions.length + 1}`,
            purpose: session?.purpose || purpose,
            venueId: session?.venue || undefined,
            date: session?.date ? dayjs(session.date) : undefined,
            slotId: session?.slotId || undefined,
        });
        setGroupModalOpen(true);
    };

    const saveGroupLocally = values => {
        const id = editingGroupId || `plan_${window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
        const venue = eventVenues.find(item => item.id === values.venueId);
        const date = values.date.format('YYYY-MM-DD');
        const purpose = values.purpose || 'competition';
        const members = assignments[editingGroupId] || [];
        if (sessions.some(session => session.id !== editingGroupId
            && (session.purpose || 'competition') === purpose
            && (assignments[session.id] || []).some(reg => members.some(member => member.id === reg.id)))) {
            message.error('A registrant is already in another group of this type. Remove the duplicate membership before changing the group type.');
            return;
        }
        // Attendance groups never link a performer session, so they create no time or seat inventory.
        const slotId = purpose === ATTENDANCE_PURPOSE ? null : values.slotId || null;
        const slot = (planningState?.draftSlots || []).find(item => item.slotId === slotId);
        if (slotId && (!slot || slot.venueId !== values.venueId || slot.date !== date
            || sessions.some(item => item.id !== editingGroupId && item.slotId === slotId))) {
            message.error('Select an unused session for this venue and date.');
            return;
        }
        const start = slot?.start || null;
        const end = slot?.end || null;
        const time = start && end ? `${start}-${end}` : '';
        const next = {
            id, label: values.label.trim(), purpose, venue: values.venueId, venueLabel: venue?.label || values.venueId,
            date, slotId, start, end, time,
            name: `${values.label.trim()} — ${venue?.label || values.venueId} — ${date} — ${purpose === ATTENDANCE_PURPOSE ? 'Orchestra attendance' : time || 'Session pending'}`,
        };
        setSessions(previous => editingGroupId
            ? previous.map(item => item.id === editingGroupId ? next : item)
            : [...previous, next]);
        if (!editingGroupId) {
            setAssignments(previous => ({ ...previous, [id]: [] }));
            setExpandedSessions(previous => ({ ...previous, [id]: true }));
        }
        setPlanningPreview(null);
        setSwapSelections([]);
        setGroupModalOpen(false);
        groupForm.resetFields();
    };

    const removeGroup = session => Modal.confirm({
        title: `Delete ${session.label}?`,
        content: isAttendanceGroup(session)
            ? 'This removes only attendance membership. Performance assignments will stay in place.'
            : 'This removes only performance membership. Orchestra attendance assignments will stay in place.',
        onOk: () => {
            setSessions(previous => previous.filter(item => item.id !== session.id));
            setAssignments(previous => {
                const next = { ...previous };
                delete next[session.id];
                return next;
            });
            setPlanningPreview(null);
        },
    });

    const moveGroup = (index, direction) => {
        const sectionIndices = sessions.map((session, sessionIndex) => ({ session, sessionIndex }))
            .filter(item => isAttendanceGroup(item.session) === isAttendanceGroup(sessions[index]))
            .map(item => item.sessionIndex);
        const other = sectionIndices[sectionIndices.indexOf(index) + direction];
        if (other === undefined) return;
        setSessions(previous => {
            const next = [...previous];
            [next[index], next[other]] = [next[other], next[index]];
            return next;
        });
        setPlanningPreview(null);
    };

    // --- Save to Firestore ---
    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            if (isPlanning) {
                if (readOnly) throw new Error('Published groups are read-only.');
                const groups = sessions.map((session, index) => ({
                    groupId: session.id, label: session.label, purpose: session.purpose || 'competition',
                    venueId: session.venue, date: session.date,
                    ordinal: index + 1, slotId: session.slotId || null, start: session.start, end: session.end,
                    registrantIds: (assignments[session.id] || []).map(registrant => registrant.id),
                }));
                const response = await apis.competitionPlanning.saveDraft(eventId, planningState.revision, groups);
                if (response?.error) throw new Error(response.error.data?.message || 'Draft save was rejected. Refresh before retrying.');
                setPlanningState(response.data);
                setInitialSessions(sessions);
                setInitialAssignments(assignments);
                setPlanningPreview(null);
                message.success('Draft board saved.');
                return;
            }
            // Serialize: store only registrantId + name + order
            const serialized = {};
            Object.keys(assignments).forEach(sessionId => {
                serialized[sessionId] = (assignments[sessionId] || []).map((r, idx) => ({
                    registrantId: r.id,
                    name: getRegistrantName(r),
                    displayName: getDisplayName(r),
                    email: getRegistrantEmail(r),
                    order: idx,
                    competitionCategory: r.competitionCategory || '',
                    performanceCategory: r.PerformanceCategory || r.performanceCategory || '',
                    isEnsemble: isEnsembleRegistrant(r),
                    performerNames: getPerformerNames(r),
                    teacherName: r.teacherName || '',
                    repertoireTitle: r.repertoireTitle || '',
                }));
            });
            const response = await apis.session.saveAssignments({
                eventId,
                assignments: serialized,
            });
            if (response?.error) throw new Error(response.error.data?.error || 'Assignment save was rejected.');
            message.success('Assignments saved successfully!');
            setInitialAssignments(assignments);
        } catch (err) {
            console.error('Error saving:', err);
            message.error(err.message || 'Failed to save assignments');
        } finally {
            setSaving(false);
        }
    }, [assignments, eventId, isPlanning, planningState, readOnly, sessions]);

    // --- Discard changes ---
    const handleDiscard = useCallback(() => {
        Modal.confirm({
            title: 'Discard all unsaved changes?',
            content: 'This will revert all assignments to the last saved state.',
            okText: 'Discard',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                setAssignments(initialAssignments);
                setSessions(initialSessions);
                setSwapSelections([]);
                message.info('Changes discarded');
            },
        });
    }, [initialAssignments, initialSessions]);

    // --- Clear all ---
    const handleClearAll = useCallback(() => {
        Modal.confirm({
            title: 'Clear all assignments?',
            content: 'This will clear both performance and orchestra attendance assignments. Save to apply these changes.',
            okText: 'Clear All',
            okType: 'danger',
            onOk() {
                setAssignments({});
                message.info('All assignments cleared locally');
            },
        });
    }, []);

    // --- Change Event with confirmation ---
    const handleEventChange = useCallback((newId) => {
        if (isDirty) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes in the current event. Changing events will discard these changes. Continue?',
                okText: 'Discard & Change',
                okType: 'danger',
                onOk() {
                    setEventId(newId);
                },
            });
        } else {
            setEventId(newId);
        }
    }, [isDirty]);

    const previewSchedule = async () => {
        if (isDirty) { message.warning('Save the draft board before previewing.'); return; }
        setPlanningBusy(true);
        try {
            const response = await apis.competitionPlanning.preview(eventId);
            if (response?.error) throw new Error(response.error.data?.message || 'Could not preview schedule.');
            setPlanningPreview(response.data);
        } catch (error) { message.error(error.message); }
        finally { setPlanningBusy(false); }
    };

    const startPlanning = () => Modal.confirm({
        title: `Start competition planning for ${eventId}?`,
        content: 'This creates an empty private draft. Existing sessions, assignments or ticket activity must be reconciled first.',
        onOk: async () => {
            setPlanningBusy(true);
            try {
                const response = await apis.competitionPlanning.saveDraft(eventId, 0, []);
                if (response?.error) throw new Error(response.error.data?.message || 'Planning could not start.');
                setReloadVersion(previous => previous + 1);
            } catch (error) { message.error(error.message); }
            finally { setPlanningBusy(false); }
        },
    });

    const publishSchedule = () => Modal.confirm({
        title: 'Publish all competition sessions?',
        content: 'This fixes every group time and creates the ticketing assignments. Generate seats and verify readiness before sales.',
        onOk: async () => {
            setPlanningBusy(true);
            try {
                const response = await apis.competitionPlanning.publish(eventId, planningState.revision);
                if (response?.error) throw new Error(response.error.data?.message || 'Publication was rejected.');
                message.success(`Published ${response.data.publishedSlots} sessions.`);
                setReloadVersion(previous => previous + 1);
            } catch (error) { message.error(error.message); }
            finally { setPlanningBusy(false); }
        },
    });

    const generatePlanningSeats = () => Modal.confirm({
        title: 'Generate seats for published sessions?',
        onOk: async () => {
            setPlanningBusy(true);
            try {
                for (const group of planningState.groups.filter(item => !isAttendanceGroup(item))) {
                    await uploadFullSeatLayout(eventId, group.venueId, [`${group.date}_${group.start}-${group.end}`]);
                }
                message.success('Seat generation completed.');
            } catch (error) { message.error(error.message || 'Seat generation failed.'); }
            finally { setPlanningBusy(false); }
        },
    });

    const verifyPlanningSeats = async () => {
        setPlanningBusy(true);
        try {
            const response = await apis.competitionPlanning.markReady(eventId);
            if (response?.error) throw new Error(response.error.data?.message || 'Seat verification failed.');
            if (!response.data.ready) message.error(response.data.issues?.join(' ') || 'Seat inventory is incomplete.');
            else {
                message.success('Competition sessions are ready for ticket sales.');
                setReloadVersion(previous => previous + 1);
            }
        } catch (error) { message.error(error.message); }
        finally { setPlanningBusy(false); }
    };

    // --- Export to Excel ---
    const handleExport = useCallback(() => {
        const workbook = new ExcelJS.Workbook();
        sessions.forEach((session, index) => {
            const regs = assignments[session.id] || [];
            const sheetName = `${index + 1}_${session.label || session.venue || 'Session'}`
                .replace(/[^A-Za-z0-9 _-]/g, '-').slice(0, 31);
            const ws = workbook.addWorksheet(sheetName);
            ws.addRow(['#', 'Name', 'Email', 'Type', 'Performer Names', 'Teacher', 'Category', 'Achievement']);
            ws.getRow(1).font = { bold: true };
            regs.forEach((r, i) => {
                const isEnsemble = isEnsembleRegistrant(r);
                const pNames = getPerformerNames(r);
                ws.addRow([
                    i + 1,
                    getDisplayName(r),
                    getRegistrantEmail(r),
                    isEnsemble ? `Ensemble (${pNames.length})` : 'Solo',
                    pNames.join(', '),
                    r.teacherName || '',
                    r.competitionCategory || '',
                    r.finalAward || r.achievement || '',
                ]);
            });
            ws.columns.forEach(col => { col.width = 20; });
        });
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            FileSaver.saveAs(blob, `SessionAssignments_${eventId}.xlsx`);
        });
    }, [sessions, assignments, eventId]);

    const handleExportTeacherStats = async () => {
        if (isTeacherStatsExporting || visibleTeacherStats.length === 0) return;
        setIsTeacherStatsExporting(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Teacher Award Stats');
            worksheet.columns = teacherStatsColumns.map(column => ({
                key: column.dataIndex,
                width: column.key === 'name' ? 36 : Math.max(18, column.title.length + 2),
            }));
            worksheet.addRow(['Event', eventId]);
            worksheet.addRow(['Competition category', teacherStatsCategory || 'All competition categories']);
            worksheet.addRow(['Teacher search', teacherStatsSearch.trim() || 'All teachers']);
            worksheet.addRow([]);
            const header = worksheet.addRow(teacherStatsColumns.map(column => column.title));
            header.font = { bold: true, color: { argb: 'FFEBBC64' } };
            header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1E1E' } };
            worksheet.addRows(visibleTeacherStats);
            const totalRow = worksheet.addRow({ name: 'Filtered total', ...teacherStatsTotals });
            totalRow.font = { bold: true };
            worksheet.views = [{ state: 'frozen', ySplit: header.number }];
            worksheet.autoFilter = {
                from: { row: header.number, column: 1 },
                to: { row: header.number + visibleTeacherStats.length, column: teacherStatsColumns.length },
            };
            const buffer = await workbook.xlsx.writeBuffer();
            const safeEventId = String(eventId).replace(/[^A-Za-z0-9_-]/g, '_');
            FileSaver.saveAs(new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }), `TEACHER_AWARD_STATS_${safeEventId}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
            message.success(`Exported ${visibleTeacherStats.length} teachers to Excel.`);
        } catch (error) {
            console.error('Teacher award statistics export failed:', error);
            message.error('Could not export teacher award statistics. Please try again.');
        } finally {
            setIsTeacherStatsExporting(false);
        }
    };

    // --- Toggle session expand ---
    const toggleSession = useCallback((sessionId) => {
        setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
    }, []);

    // --- Render ---
    if (loading) {
        return <div className="sam-loading-overlay"><Spin size="large" /><span>Loading session data...</span></div>;
    }
    if (loadError) return <Alert type="error" showIcon message="Session board unavailable" description={loadError} action={<Button onClick={() => setReloadVersion(previous => previous + 1)}>Retry</Button>} />;
    if (loadedEventId !== eventId) return <div className="sam-loading-overlay"><Spin size="large" /><span>Loading session data...</span></div>;

    const totalAssigned = assignedIds.size;
    const totalUnassigned = eligibleUnassignedRegistrants.length;

    return (
        <DndProvider backend={HTML5Backend}>
            <div>
                {isPlanning && <Alert showIcon type={planningState.status === 'ready' ? 'success' : 'info'}
                    message={`Competition schedule: ${planningState.status} (revision ${planningState.revision})`}
                    description={readOnly
                        ? 'Published groups are read-only. Competition sales open only after numbered seats are verified and the schedule is ready.'
                        : 'Arrange performers in draft groups, then link each group to a session from Performer Sessions. Final times are required before publication; numbered seats must be verified before ticket sales.'}
                    style={{ marginBottom: 12 }} />}
                {/* === Controls Bar === */}
                <div className="sam-controls-bar">
                    <AttendanceStatusFilter value={attendanceFilter} onChange={value => {
                        setAttendanceFilter(value);
                        setSwapSelections([]);
                    }} />
                    <Select
                        value={eventId}
                        onChange={handleEventChange}
                        style={{ width: 180 }}
                        options={[
                            { value: 'APCS2026', label: 'APCS 2026' },
                            { value: 'APCS2025', label: 'APCS 2025' },
                        ]}
                    />
                    {isDirty && (
                        <Tag color="orange" className="sam-editing-indicator">
                            <ThunderboltOutlined spin /> Editing Mode
                        </Tag>
                    )}
                    <div className="sam-stats-row">
                        <div className="sam-stat-badge total">📊 Event registrations: {allRegistrants.length}</div>
                        <div className="sam-stat-badge assigned">✅ Performance assigned: {totalAssigned}</div>
                        <div className="sam-stat-badge unassigned">⏳ Event unassigned: {totalUnassigned}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        {!isPlanning && <Button onClick={startPlanning} loading={planningBusy} disabled={isDirty}>Start planning</Button>}
                        {isPlanning && !readOnly && <Button onClick={() => openGroup()} disabled={saving || planningBusy}>Add performance group</Button>}
                        <Button icon={<BarChartOutlined />} onClick={() => {
                            setOverviewVenue(overviewVenues.length > 0 ? overviewVenues[0].id : null);
                            setIsOverviewModalOpen(true);
                        }}>
                            Overview
                        </Button>
                        <Button icon={<TeamOutlined />} onClick={() => {
                            setTeacherStatsSearch('');
                            setTeacherStatsCategory(null);
                            setIsTeacherStatsOpen(true);
                        }}>
                            Teacher Stats
                        </Button>
                        {!readOnly && <Button icon={<SwapOutlined />}
                            type={isSwapMode ? 'primary' : 'default'}
                            onClick={() => { setIsSwapMode(!isSwapMode); setSwapSelections([]); }}
                            style={isSwapMode ? { background: '#8b5cf6', borderColor: '#8b5cf6' } : {}}>
                            {isSwapMode ? 'Exit Swap' : 'Swap Mode'}
                        </Button>}
                        {!readOnly && <Button icon={<UndoOutlined />} onClick={handleClearAll}>Clear All</Button>}
                        {isDirty && (
                            <Button icon={<CloseOutlined />} onClick={handleDiscard}>Discard</Button>
                        )}
                        {!readOnly && <Button icon={<SaveOutlined />} type="primary" loading={saving} onClick={handleSave}
                            style={isDirty ? { background: '#10b981', borderColor: '#10b981', boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)' } : {}}>
                            Save {isDirty ? 'Changes' : ''}
                        </Button>}
                        <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
                    </div>
                </div>

                {isPlanning && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
                    {planningState.status === 'draft' && <>
                        <Button onClick={previewSchedule} loading={planningBusy} disabled={isDirty}>Validate and preview</Button>
                        <Button type="primary" onClick={publishSchedule} loading={planningBusy}
                            disabled={isDirty || !planningPreview?.canPublish || planningPreview.revision !== planningState.revision}>Publish schedule</Button>
                    </>}
                    {planningState.status === 'published' && <>
                        <Button onClick={generatePlanningSeats} loading={planningBusy}>Generate numbered seats</Button>
                        <Button type="primary" onClick={verifyPlanningSeats} loading={planningBusy}>Verify seats and mark ready</Button>
                    </>}
                    {planningPreview && <Alert type={planningPreview.canPublish ? 'success' : 'error'}
                        message={planningPreview.canPublish ? 'All groups are ready to publish' : 'Publication blocked'}
                        description={planningPreview.errors?.join(' ') || `${planningPreview.groups.length} groups`} />}
                </div>}

                {/* === Swap Bar === */}
                {isSwapMode && (
                    <div className="sam-swap-bar">
                        <SwapOutlined />
                        <span>
                            {swapSelections.length === 0 && 'Click two registrants to swap their positions'}
                            {swapSelections.length === 1 && 'Now click the second registrant to swap with'}
                            {swapSelections.length === 2 && 'Ready to swap!'}
                        </span>
                        {swapSelections.length === 2 && (
                            <Button size="small" icon={<CheckOutlined />} onClick={executeSwap}
                                style={{ background: '#fff', color: '#6366f1', fontWeight: 700 }}>
                                Confirm Swap
                            </Button>
                        )}
                    </div>
                )}

                <h3>Performance Sessions</h3>
                {/* === Two-Panel Layout === */}
                <div className="sam-layout">
                    {/* --- Left Panel: Unassigned Pool --- */}
                    <div className="sam-pool-panel">
                        <div className="sam-pool-header">
                            {isPlanning && eventId === 'APCS2026' && <Tabs size="small" activeKey={registrantTab}
                                onChange={key => { setRegistrantTab(key); setSearchTerm(''); setCategoryFilter(null); }}
                                items={[
                                    { key: 'current', label: `${eventId} Registrants` },
                                    { key: 'other', label: 'Other Event Registrants' },
                                ]} />}
                            <h3>
                                Unassigned Registrants
                                <span className="count-badge">{unassignedRegistrants.length}</span>
                            </h3>
                            {registrantTab === 'other' && <div style={{ marginBottom: 10 }}>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    <Select aria-label="Source event" value={sourceEventId} onChange={setSourceEventId}
                                        options={[{ value: 'APCS2025', label: 'APCS 2025' }]} style={{ flex: 1 }} />
                                    <Button onClick={loadOtherEvent} loading={guestsLoading}>
                                        {guestsLoaded ? 'Refresh' : 'Load registrants'}
                                    </Button>
                                </div>
                                <p style={{ fontSize: 12, margin: 0 }}>Missing event ID means APCS2025. Drag into a performance group, or use the orchestra attendee selector after loading. Original registrations stay unchanged.</p>
                                {guestLoadError && <Alert type="error" showIcon message={guestLoadError} style={{ marginTop: 8 }} />}
                            </div>}
                            <div className="sam-pool-filters">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search kid, ensemble, or teacher..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    allowClear
                                    size="small"
                                    style={{ flex: 1 }}
                                />
                                <Select
                                    placeholder="Category"
                                    value={categoryFilter}
                                    onChange={setCategoryFilter}
                                    allowClear
                                    size="small"
                                    style={{ width: 140 }}
                                    options={poolCategoryOptions}
                                />
                            </div>
                        </div>
                        <div className="sam-pool-list">
                            {registrantTab === 'other' && guestsLoading && <Spin style={{ margin: 12 }} />}
                            {unassignedRegistrants.length === 0 ? (
                                <div className="sam-pool-empty">
                                    <InboxOutlined />
                                    <span>
                                        {registrantTab === 'other' && !guestsLoaded
                                            ? 'Load the source event to browse registrants'
                                            : sourceRegistrants.length === 0
                                            ? 'No registrants found for this event'
                                            : eligibleSourceRegistrants.length === 0
                                                ? 'No eligible unassigned registrants'
                                                : 'No registrants match the current filters'}
                                    </span>
                                </div>
                            ) : (
                                unassignedRegistrants.map((reg) => (
                                    <DraggableCard
                                        key={reg.id}
                                        registrant={reg}
                                        index={0}
                                        sessionId={null}
                                        moveWithinSession={() => { }}
                                        isSwapMode={false}
                                        isSwapSelected={false}
                                        onSwapSelect={() => { }}
                                        onPlayVideo={handlePlayVideo}
                                        searchTerm={searchTerm}
                                        readOnly={readOnly}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* --- Right Panel: Sessions --- */}
                    <div className="sam-sessions-panel">
                        {performanceSessions.length === 0 ? (
                            <div className="sam-pool-empty" style={{ padding: 60 }}>
                                <InboxOutlined />
                                <span>{isPlanning ? 'No groups yet. Add a group with a venue and date to begin.' : <>No sessions found in <strong>events/{eventId}</strong></>}</span>
                            </div>
                        ) : (
                            performanceSessions.map((session, idx) => (
                                <div key={session.id}>
                                {isPlanning && !readOnly && <div style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
                                    <Button size="small" onClick={() => openGroup(session)}>Edit group/session</Button>
                                    <Button size="small" onClick={() => moveGroup(sessions.indexOf(session), -1)} disabled={idx === 0}>Up</Button>
                                    <Button size="small" onClick={() => moveGroup(sessions.indexOf(session), 1)} disabled={idx === performanceSessions.length - 1}>Down</Button>
                                    <Button size="small" danger onClick={() => removeGroup(session)}>Delete</Button>
                                </div>}
                                <SessionDropZone
                                    key={session.id}
                                    sessionId={session.id}
                                    sessionName={session.name}
                                    sessionTime={session.time}
                                    sessionColor={SESSION_COLORS[idx % SESSION_COLORS.length]}
                                    registrants={assignments[session.id] || []}
                                    index={idx}
                                    moveWithinSession={moveWithinSession}
                                    onDropFromPool={handleDrop}
                                    onRemove={readOnly ? null : handleRemove}
                                    onPlayVideo={handlePlayVideo}
                                    isExpanded={!!expandedSessions[session.id]}
                                    onToggle={() => toggleSession(session.id)}
                                    isSwapMode={isSwapMode}
                                    swapSelections={swapSelections}
                                    onSwapSelect={handleSwapSelect}
                                    searchTerm={searchTerm}
                                    attendanceFilter={attendanceFilter}
                                    readOnly={readOnly}
                                />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {isPlanning && <OrchestraAttendanceTable key={eventId} groups={attendanceSessions} performanceGroups={performanceSessions}
                    assignments={assignments} registrants={eligibleAttendanceRegistrants} getName={getDisplayName}
                    matchesRegistrant={doesRegistrantMatchSearch}
                    attendanceFilter={attendanceFilter}
                    readOnly={readOnly} onMembersChange={handleAttendanceMembersChange}
                    onAdd={() => openGroup(null, ATTENDANCE_PURPOSE)} onEdit={openGroup} onRemove={removeGroup} />}

                {activeVideo && (
                    <Modal title={`Performance video — ${getDisplayName(activeVideo.registrant)}`}
                        open width={900} zIndex={1100} destroyOnClose
                        onCancel={() => setActiveVideo(null)}
                        footer={<Button onClick={() => setActiveVideo(null)}>Close</Button>}>
                        {videoError && <Alert type="error" showIcon
                            message="The performance video could not be played."
                            description="Try opening it in a new tab. If it still fails, check the saved video file."
                            style={{ marginBottom: 12 }} />}
                        <video key={activeVideo.url} src={activeVideo.url} controls autoPlay playsInline
                            preload="metadata" onError={() => setVideoError(true)}
                            style={{ display: 'block', width: '100%', maxHeight: '65vh', background: '#000' }}>
                            Your browser does not support video playback.
                        </video>
                        <a href={activeVideo.url} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-block', marginTop: 12 }}>Open video in a new tab</a>
                    </Modal>
                )}

                <Modal title={editingGroupId ? 'Edit planning group' : 'Add planning group'} open={groupModalOpen}
                    onCancel={() => { setGroupModalOpen(false); groupForm.resetFields(); }}
                    onOk={() => groupForm.submit()} okText="Keep on board" destroyOnHidden>
                    <Form form={groupForm} layout="vertical" onFinish={saveGroupLocally}>
                        <Form.Item name="label" label="Group name" rules={[{ required: true, whitespace: true }]}>
                            <Input maxLength={80} />
                        </Form.Item>
                        <Form.Item name="purpose" label="Group type" rules={[{ required: true }]}
                            extra={selectedGroupPurpose === ATTENDANCE_PURPOSE
                                ? 'Attendance is assigned separately below the performance board. Members with a performance group remain ticketable. This does not grant tickets or reserve admission.'
                                : undefined}>
                            <Select options={GROUP_PURPOSE_OPTIONS}
                                onChange={value => { if (value === ATTENDANCE_PURPOSE) groupForm.setFieldsValue({ slotId: undefined }); }} />
                        </Form.Item>
                        <Form.Item name="venueId" label="Venue" rules={[{ required: true }]}>
                            <Select options={eventVenues.map(venue => ({ value: venue.id, label: venue.label || venue.id }))} />
                        </Form.Item>
                        <Form.Item name="date" label={selectedGroupPurpose === ATTENDANCE_PURPOSE ? 'Attendance date' : 'Performance date'} rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        {selectedGroupPurpose !== ATTENDANCE_PURPOSE && (
                            <Form.Item name="slotId" label="Performer session (optional until publication)">
                                <Select allowClear placeholder="Select a session from Performer Sessions"
                                    options={(planningState?.draftSlots || [])
                                        .filter(slot => slot.venueId === selectedGroupVenue && slot.date === selectedGroupDate?.format('YYYY-MM-DD'))
                                        .map(slot => ({
                                            value: slot.slotId,
                                            label: `${slot.date} — ${slot.start && slot.end ? `${slot.start}-${slot.end}` : 'Time pending'}`,
                                            disabled: sessions.some(item => item.id !== editingGroupId && item.slotId === slot.slotId),
                                        }))} />
                            </Form.Item>
                        )}
                    </Form>
                </Modal>

                <Modal
                    title={`Teacher Award Stats — ${eventId}`}
                    open={isTeacherStatsOpen}
                    onCancel={() => setIsTeacherStatsOpen(false)}
                    footer={null}
                    width={1050}
                >
                    <p style={{ color: '#666', marginBottom: 12 }}>
                        Counts include assigned and unassigned registrants for this event and the selected competition category. Each registration counts once, including ensembles.
                        {' '}Totals cover all teachers matching the filters across every page. Registrations without a teacher are excluded. Winners include Sapphire, Diamond, Gold and Silver; the overall total also includes Fail and Other / no award.
                    </p>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="Search teacher"
                            value={teacherStatsSearch}
                            onChange={event => setTeacherStatsSearch(event.target.value)}
                            allowClear
                            style={{ width: 320, maxWidth: '100%' }}
                        />
                        <Select
                            aria-label="Filter teacher awards by competition category"
                            placeholder="All competition categories"
                            value={teacherStatsCategory}
                            onChange={setTeacherStatsCategory}
                            options={categoryOptions}
                            allowClear
                            style={{ width: 240, maxWidth: '100%' }}
                        />
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportTeacherStats}
                            loading={isTeacherStatsExporting}
                            disabled={visibleTeacherStats.length === 0}
                            style={{ background: '#1E1E1E', borderColor: '#EBBC64', color: '#EBBC64' }}
                        >
                            Export to Excel
                        </Button>
                    </div>
                    <Table
                        rowKey="key"
                        columns={teacherStatsColumns}
                        dataSource={visibleTeacherStats}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        scroll={{ x: 1210 }}
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row style={{ background: '#1E1E1E', color: '#EBBC64', fontWeight: 600 }}>
                                    {teacherStatsColumns.map((column, index) => (
                                        <Table.Summary.Cell key={column.key} index={index}>
                                            {column.key === 'name' ? 'Filtered total' : teacherStatsTotals[column.dataIndex]}
                                        </Table.Summary.Cell>
                                    ))}
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                        locale={{ emptyText: 'No teachers found for these filters' }}
                    />
                </Modal>

                {/* === Overview Modal === */}
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32, gap: 16, flexWrap: 'wrap' }}>
                            <span>Session Overview</span>
                            {overviewTab === 'registrants' && <AttendanceStatusFilter value={attendanceFilter} onChange={value => {
                                setAttendanceFilter(value);
                                setSwapSelections([]);
                            }} />}
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder="Search kid, ensemble, or teacher in overview..."
                                value={overviewSearchTerm}
                                onChange={e => setOverviewSearchTerm(e.target.value)}
                                allowClear
                                size="small"
                                style={{ width: 340, fontWeight: 'normal' }}
                            />
                        </div>
                    }
                    open={isOverviewModalOpen}
                    onCancel={() => setIsOverviewModalOpen(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsOverviewModalOpen(false)}>
                            Close
                        </Button>
                    ]}
                    width="95vw"
                    style={{ top: 20 }}
                    styles={{ body: { maxHeight: '80vh', overflowY: 'auto', padding: '16px' } }}
                >
                    {/* Venue Tabs */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        {overviewVenues.map(v => (
                            <Button
                                key={v.id}
                                type={overviewVenue === v.id ? 'primary' : 'default'}
                                onClick={() => setOverviewVenue(v.id)}
                                style={overviewVenue === v.id
                                    ? { background: '#6366f1', borderColor: '#6366f1' }
                                    : {}}
                            >
                                {v.label}
                            </Button>
                        ))}
                    </div>

                    <Tabs
                        activeKey={overviewTab}
                        onChange={setOverviewTab}
                        items={[
                            { key: 'registrants', label: 'Registrants' },
                            { key: 'teachers', label: 'Teachers' },
                        ]}
                    />

                    {!overviewVenue ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                            Select a venue above to view its sessions.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 8 }}>
                            {overviewSessionsForVenue.map((session, idx) => {
                                const regs = assignments[session.id] || [];
                                const visibleRegs = getVisibleAttendanceEntries(regs, attendanceFilter);
                                const teacherNames = new Map();
                                regs.forEach(reg => {
                                    const name = (reg.teacherName || '').trim();
                                    if (name && !teacherNames.has(name.toLocaleLowerCase())) {
                                        teacherNames.set(name.toLocaleLowerCase(), name);
                                    }
                                });
                                const teachers = Array.from(teacherNames.values());
                                const sessionColor = SESSION_COLORS[idx % SESSION_COLORS.length];
                                const matchingOverviewCount = overviewSearchTerm
                                    ? overviewTab === 'teachers'
                                        ? teachers.filter(name => name.toLocaleLowerCase().includes(overviewSearchTerm.trim().toLocaleLowerCase())).length
                                        : visibleRegs.filter(({ registrant }) => doesRegistrantMatchSearch(registrant, overviewSearchTerm)).length
                                    : 0;
                                return (
                                    <div key={session.id} style={{
                                        minWidth: 280, maxWidth: 320, flex: '0 0 auto',
                                        background: '#f7f8fa', borderRadius: 10,
                                        border: matchingOverviewCount > 0 ? '2px solid #f59e0b' : '1px solid #e8e8e8',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Column Header */}
                                        <div style={{
                                            padding: '10px 14px',
                                            background: `linear-gradient(135deg, ${sessionColor}22, ${sessionColor}11)`,
                                            borderBottom: `3px solid ${sessionColor}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{session.date}</div>
                                                <div style={{ fontSize: 12, color: '#555' }}>{session.time || 'Time pending'}</div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                {matchingOverviewCount > 0 && (
                                                    <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>
                                                        {matchingOverviewCount} found
                                                    </Tag>
                                                )}
                                                <Tag color="blue" style={{ marginRight: 0 }}>
                                                    {overviewTab === 'teachers' ? teachers.length : regs.length}
                                                </Tag>
                                                {overviewTab === 'registrants' && attendanceFilter !== 'all' && <Tag>{visibleRegs.length} shown</Tag>}
                                            </div>
                                        </div>
                                        <GroupRegistrantSummary registrants={regs} groupName={session.name} />
                                        {/* Registrants or unique teachers for this session */}
                                        <div style={{ padding: '8px 10px', maxHeight: 500, overflowY: 'auto' }}>
                                            {overviewTab === 'teachers' ? (
                                                teachers.length === 0 ? (
                                                    <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', padding: 20 }}>No teachers listed</div>
                                                ) : teachers.map(name => {
                                                    const isMatch = Boolean(overviewSearchTerm.trim() && name.toLocaleLowerCase().includes(overviewSearchTerm.trim().toLocaleLowerCase()));
                                                    return (
                                                        <div key={name.toLocaleLowerCase()} style={{
                                                            padding: '8px 10px', marginBottom: 6,
                                                            background: isMatch ? '#fffbeb' : '#fff',
                                                            border: isMatch ? '1.5px solid #f59e0b' : '1px solid #eee',
                                                            borderRadius: 8, color: '#1a1a2e', fontSize: 12, fontWeight: 600,
                                                        }}>
                                                            {name}
                                                        </div>
                                                    );
                                                })
                                            ) : visibleRegs.length === 0 ? (
                                                <div style={{ color: '#aaa', fontSize: 12, textAlign: 'center', padding: 20 }}>{regs.length ? 'No registrants match this attendance filter' : 'No registrants'}</div>
                                            ) : (
                                                visibleRegs.map(({ registrant: reg, index: i }) => {
                                                    const isEnsemble = isEnsembleRegistrant(reg);
                                                    const pNames = getPerformerNames(reg);
                                                    const displayName = getDisplayName(reg);
                                                    const isMatch = Boolean(overviewSearchTerm && doesRegistrantMatchSearch(reg, overviewSearchTerm));
                                                    const award = reg.finalAward || reg.achievement || '';
                                                    const awardLower = award.toLowerCase();
                                                    const awardColor =
                                                        awardLower === 'sapphire' ? '#2563eb' :
                                                        awardLower === 'diamond' ? '#7c3aed' :
                                                        awardLower === 'gold' ? '#d97706' :
                                                        awardLower === 'silver' ? '#6b7280' :
                                                        awardLower === 'bronze' ? '#b45309' : '#888';
                                                    return (
                                                        <div key={reg.id} style={{
                                                            display: 'flex', alignItems: 'flex-start', gap: 8,
                                                            padding: '8px 10px', marginBottom: 6,
                                                            background: isMatch ? '#fffbeb' : '#fff', borderRadius: 8,
                                                            border: isMatch ? '1.5px solid #f59e0b' : '1px solid #eee',
                                                            boxShadow: isMatch ? '0 0 8px rgba(245, 158, 11, 0.2)' : 'none',
                                                            fontSize: 12
                                                        }}>
                                                            <span style={{
                                                                minWidth: 20, height: 20, borderRadius: '50%',
                                                                background: sessionColor, color: '#fff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2
                                                            }}>{i + 1}</span>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                    <span style={{ fontWeight: 700, color: '#1a1a2e' }}>
                                                                        {displayName}
                                                                    </span>
                                                                    <PlayVideoButton registrant={reg} onPlay={handlePlayVideo} />
                                                                    <AttendanceStatusChip registrant={reg} />
                                                                    {isEnsemble && (
                                                                        <Tag color="purple" style={{ fontSize: 10, padding: '0 5px', lineHeight: '18px', margin: 0 }}>
                                                                            Ensemble ({pNames.length})
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                                {isEnsemble && pNames.length > 0 && (
                                                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                                                        {pNames.map((kid, kIdx) => {
                                                                            const isKidMatch = Boolean(overviewSearchTerm && kid.toLowerCase().includes(overviewSearchTerm.toLowerCase().trim()));
                                                                            return (
                                                                                <span key={kIdx} style={{
                                                                                    fontSize: 10,
                                                                                    background: isKidMatch ? '#fef08a' : '#f3e8ff',
                                                                                    color: isKidMatch ? '#854d0e' : '#7e22ce',
                                                                                    fontWeight: isKidMatch ? 700 : 500,
                                                                                    padding: '1px 6px',
                                                                                    borderRadius: 4,
                                                                                    border: isKidMatch ? '1px solid #eab308' : '1px solid #e9d5ff'
                                                                                }}>
                                                                                    {kid}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                                                    {award && (
                                                                        <span style={{
                                                                            display: 'inline-block', padding: '1px 6px',
                                                                            borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                                            background: `${awardColor}18`, color: awardColor,
                                                                            border: `1px solid ${awardColor}33`
                                                                        }}>{award}</span>
                                                                    )}
                                                                </div>
                                                                <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>
                                                                    Age category: {getAgeCategoryLabel(reg.ageCategory)}
                                                                </div>
                                                                {reg.teacherName && (
                                                                    <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>🎓 {reg.teacherName}</div>
                                                                )}
                                                                {reg.repertoireTitle && (
                                                                    <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>🎼 {reg.repertoireTitle}</div>
                                                                )}
                                                                {reg.competitionCategory && (
                                                                    <div style={{ color: '#888', fontSize: 10, marginTop: 1 }}>🎵 {reg.competitionCategory}</div>
                                                                )}
                                                                {getRegistrantCity(reg) && (
                                                                    <div style={{ color: '#888', fontSize: 10, marginTop: 1 }}>📍 {getRegistrantCity(reg)}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal>
            </div>
        </DndProvider>
    );
};

export default SessionAssignmentManager;
